import {
  Box,
  Dialog,
  IconButton,
  Typography,
  Chip,
  Stack,
  Divider,
  Button,
  TextField,
  Tooltip,
  InputBase,
  alpha,
  Collapse,
} from "@mui/material";
import { ExpandMore, ExpandLess } from "@mui/icons-material";
import CloseIcon from "@mui/icons-material/Close";
import DeleteIcon from "@mui/icons-material/Delete";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import VisibilityOffOutlinedIcon from "@mui/icons-material/VisibilityOffOutlined";
import SendIcon from "@mui/icons-material/Send";
import { useState, useMemo, useEffect } from "react";
import { useGetOne, useDelete, useUpdate, useRefresh, useNotify, usePermissions, useGetIdentity } from "react-admin";
import { ResourceName, TaskStatus, AlertType, type Task, DateYMDString } from "@/types";
import { RecordComments } from "@/components/common/comments/RecordComments";
import { countComments } from "@/components/common/comments/countComments";
import { useComments } from "@/hooks/useComments";
import { useRichApiErrorNotify } from "@/hooks/useRichApiErrorNotify";
import { canSendPandaDoc } from "../panda-doc/PandaDocTemplateRegistry";
import { PandaDocExpansion } from "../panda-doc/PandaDocExpansion";
import { ConfirmationDialog } from "@/components/ui/ConfirmationDialog";
import { getPandaDocStatusDisplay } from "@/utils/formatters/getPandaDocStatusDisplay";
import { StatusChip } from "@/components/common/fields/StatusChipField";
import { canActOnRecord } from "@/utils/permissions";
import { ColorType, ConfirmLabelType } from "@/types/shared/uiComponents";
import { AssigneeSelector } from "../shared/AssigneeSelector";
import { DueDateSelector } from "../shared/DueDateSelector";
import { RecordSelector } from "../shared/RecordSelector";
import { StatusSelector } from "../shared/StatusSelector";
import { getFilledChipStyles, getEmptyChipStyles } from "../shared/taskChipStyles";
import { DeductibleReviewTaskAction } from "../shared/DeductibleReviewTaskAction";
import { isDeductibleReviewTask, taskRecordPath } from "../../utils/taskRecordLink";

interface TaskDetailsDialogProps {
  task?: Task;
  open: boolean;
  onClose: () => void;
}

export function TaskDetailsDialog({ task: taskProp, open, onClose }: TaskDetailsDialogProps) {
  const { permissions } = usePermissions();
  const { data: identity } = useGetIdentity();
  const notify = useNotify();
  const notifyError = useRichApiErrorNotify();
  const refresh = useRefresh();
  const [update] = useUpdate();
  const [deleteOne, { isPending: isDeleting }] = useDelete();

  // Fetch fresh task data so it updates after edits
  const { data: task, refetch } = useGetOne<Task>(
    ResourceName.TASKS,
    { id: taskProp?.id ?? "" },
    { enabled: !!taskProp?.id && open }
  );

  // Scope-aware permission checks — "assigned" scope restricts to tasks assigned to the current user
  const userId = identity?.id as string | undefined;
  const canEdit = canActOnRecord(permissions, ResourceName.TASKS, "edit", task, userId);
  const canDelete = canActOnRecord(permissions, ResourceName.TASKS, "delete", task, userId);

  // Local edit state - always used for display to avoid flash on save
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [isEditingTitle, setIsEditingTitle] = useState(false);
  const [isEditingDescription, setIsEditingDescription] = useState(false);

  // PandaDoc state
  const [showPandaDocForm, setShowPandaDocForm] = useState(false);

  // Other state
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);

  // Comments are collapsed by default in the task dialog (legacy task-UX), revealed via
  // the "Comments (N)" header. The count comes from the shared useComments query
  // (same query key as RecordComments below — react-query dedupes to one fetch).
  const [commentsExpanded, setCommentsExpanded] = useState(false);
  const { comments: taskComments } = useComments(ResourceName.TASKS, task?.id ? String(task.id) : undefined);

  // Only sync local state when a different task is loaded or dialog opens
  // This prevents the flash that occurs when syncing during save
  useEffect(() => {
    if (task) {
      setTitle(task.title || "");
      setDescription(task.description || "");
      // Reset edit states when loading a new task
      setIsEditingTitle(false);
      setIsEditingDescription(false);
    }
  }, [task?.id]);

  // Data fetching
  const { data: taskTemplate } = useGetOne(
    "task-templates",
    { id: task?.template_id_ref ?? "" },
    { enabled: !!task?.template_id_ref }
  );

  // PandaDoc logic
  const hasPandaDoc = !!task?.pandadoc;
  const pandaDocStatus = task?.pandadoc?.status;

  const canSendPandaDocResult = useMemo(() => {
    return canSendPandaDoc({
      canEdit,
      hasPandaDoc,
      isSignatureEligible: task?.is_signature_eligible ?? false,
    });
  }, [canEdit, hasPandaDoc, task?.is_signature_eligible]);

  const pandaDocStatusDisplay = pandaDocStatus ? getPandaDocStatusDisplay(pandaDocStatus) : null;

  // Update handlers
  const handleUpdate = (data: Partial<Task>) => {
    if (!task) return;

    void update(
      ResourceName.TASKS,
      {
        id: task.id,
        data: {
          ...task,
          ...data,
          comments: Array.isArray(task.comments) ? task.comments : [],
        },
        previousData: task,
      },
      {
        onSuccess: () => {
          notify("Task updated", { type: AlertType.SUCCESS });
          void refetch();
          refresh();
        },
        onError: (error) => {
          notifyError(error);
          // Revert to server values on error
          setTitle(task.title || "");
          setDescription(task.description || "");
        },
      }
    );
  };

  const handleTitleSave = () => {
    setIsEditingTitle(false);
    if (title.trim() && title !== task?.title) {
      handleUpdate({ title: title.trim() });
    } else if (!title.trim()) {
      // Reset to server value if empty
      setTitle(task?.title || "");
    }
  };

  const handleDescriptionSave = () => {
    setIsEditingDescription(false);
    if (description !== task?.description) {
      handleUpdate({ description: description.trim() });
    }
  };

  const handleStatusChange = (status: TaskStatus) => {
    handleUpdate({
      status,
      completed: status === TaskStatus.COMPLETED,
    });
  };

  const handleAssigneeChange = (userId: string | null) => {
    handleUpdate({ assigned_user_id_ref: userId ?? undefined });
  };

  const handleDueDateChange = (date: string | null) => {
    handleUpdate({ due_date: (date as DateYMDString) ?? undefined });
  };

  const handleExhibitToggle = () => {
    handleUpdate({ display_claim_exhibit: !task?.display_claim_exhibit });
  };

  const handleDelete = () => {
    if (!task) return;
    void deleteOne(
      ResourceName.TASKS,
      { id: task.id, previousData: task },
      {
        onSuccess: () => {
          setDeleteConfirmOpen(false);
          refresh();
          onClose();
        },
      }
    );
  };

  if (!task) return null;

  const filledChip = getFilledChipStyles({ clickable: canEdit });
  const emptyChip = getEmptyChipStyles({ clickable: canEdit });

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
      <Stack sx={{ p: 2.5 }} spacing={2}>
        {/* Header: Record + Actions */}
        <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
          {/* Record link */}
          <RecordSelector
            objectType={task.record?.object_type ?? null}
            objectId={task.record?.object_id ?? null}
            label={task.record?.label}
            editable={false}
            hideIfEmpty
            // A deductible-review task lands on the claimant's Deductibles tab
            // with the resolution dialog open (#4243), like the task page.
            to={isDeductibleReviewTask(task) ? taskRecordPath(task) : undefined}
          />

          {/* Actions */}
          <Stack direction="row" spacing={0.5}>
            {canDelete && (
              <IconButton
                size="small"
                onClick={() => setDeleteConfirmOpen(true)}
                disabled={isDeleting}
                sx={{ color: "text.secondary", "&:hover": { color: "error.main" } }}
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            )}
            <IconButton size="small" onClick={onClose} sx={{ color: "text.secondary" }}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </Stack>
        </Stack>

        {/* Title - always show local state to prevent flash */}
        {isEditingTitle && canEdit ? (
          <InputBase
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            onBlur={handleTitleSave}
            onKeyDown={(e) => e.key === "Enter" && handleTitleSave()}
            autoFocus
            fullWidth
            sx={{ fontSize: 20, fontWeight: 600, "& input": { p: 0 } }}
          />
        ) : (
          <Typography
            onClick={() => {
              if (canEdit) {
                setIsEditingTitle(true);
              }
            }}
            sx={{
              fontSize: 20,
              fontWeight: 600,
              cursor: canEdit ? "text" : "default",
              "&:hover": canEdit ? { bgcolor: "action.hover", borderRadius: 1, mx: -1, px: 1 } : {},
            }}
          >
            {title}
          </Typography>
        )}

        {/* Description - always show local state to prevent flash */}
        {isEditingDescription && canEdit ? (
          <TextField
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            onBlur={handleDescriptionSave}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                handleDescriptionSave();
              }
            }}
            autoFocus
            multiline
            rows={2}
            fullWidth
            size="small"
            placeholder="Add notes..."
            sx={{ "& .MuiOutlinedInput-root": { borderRadius: 1.5 } }}
          />
        ) : (
          <Typography
            variant="body2"
            color={description ? "text.secondary" : "text.disabled"}
            onClick={() => {
              if (canEdit) {
                setIsEditingDescription(true);
              }
            }}
            sx={{
              cursor: canEdit ? "text" : "default",
              whiteSpace: "pre-wrap",
              minHeight: 24,
              "&:hover": canEdit ? { bgcolor: "action.hover", borderRadius: 1, mx: -1, px: 1 } : {},
            }}
          >
            {description || (canEdit ? "Add notes..." : "No description")}
          </Typography>
        )}

        <DeductibleReviewTaskAction task={task} onNavigate={onClose} />

        {/* Metadata chips */}
        <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
          <StatusSelector value={task.status ?? TaskStatus.OPEN} onChange={handleStatusChange} disabled={!canEdit} />

          <AssigneeSelector
            value={task.assigned_user_id_ref ?? null}
            onChange={handleAssigneeChange}
            disabled={!canEdit}
          />

          <DueDateSelector
            value={task.due_date ?? null}
            onChange={handleDueDateChange}
            disabled={!canEdit}
            showOverdueStyle
          />

          {/* Claims summary visibility */}
          <Tooltip title={task.display_claim_exhibit ? "Visible on claims summary" : "Hidden from claims summary"}>
            <Chip
              icon={task.display_claim_exhibit ? <VisibilityOutlinedIcon /> : <VisibilityOffOutlinedIcon />}
              label={task.display_claim_exhibit ? "On claims summary" : "Hidden"}
              onClick={canEdit ? handleExhibitToggle : undefined}
              sx={task.display_claim_exhibit ? filledChip : emptyChip}
              size="small"
            />
          </Tooltip>
        </Stack>

        {/* PandaDoc section */}
        {(canSendPandaDocResult || hasPandaDoc) && (
          <>
            <Divider />
            <Box>
              {/* Already has PandaDoc - show status */}
              {hasPandaDoc && pandaDocStatusDisplay && (
                <Stack direction="row" alignItems="center" justifyContent="space-between">
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <SendIcon sx={{ fontSize: 18, color: "text.secondary" }} />
                    <Box>
                      <Typography variant="body2" fontWeight={500}>
                        {task.pandadoc?.document_name || "Signature Request"}
                      </Typography>
                    </Box>
                  </Stack>
                  <StatusChip label={pandaDocStatusDisplay.label} color={pandaDocStatusDisplay.color} />
                </Stack>
              )}

              {/* Can send PandaDoc - show card prompt or form */}
              {canSendPandaDocResult && !showPandaDocForm && (
                <Box
                  sx={{
                    p: 2,
                    borderRadius: 2,
                    border: "1px dashed",
                    borderColor: "divider",
                    bgcolor: (theme) => alpha(theme.palette.primary.main, 0.02),
                  }}
                >
                  <Stack direction="row" alignItems="center" justifyContent="space-between">
                    <Stack direction="row" alignItems="flex-start" spacing={1.5}>
                      <Box
                        sx={{
                          width: 36,
                          height: 36,
                          borderRadius: 1,
                          bgcolor: (theme) => alpha(theme.palette.primary.main, 0.1),
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                      >
                        <SendIcon sx={{ fontSize: 18, color: "primary.main" }} />
                      </Box>
                      <Box>
                        <Typography variant="body2" fontWeight={500}>
                          Ready to send
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {taskTemplate?.title} for {task.record?.label || "claimant"}
                        </Typography>
                      </Box>
                    </Stack>
                    <Button size="small" variant="contained" onClick={() => setShowPandaDocForm(true)}>
                      Send
                    </Button>
                  </Stack>
                </Box>
              )}

              {canSendPandaDocResult && showPandaDocForm && (
                <PandaDocExpansion
                  task={task}
                  taskTemplate={taskTemplate ? { title: taskTemplate.title } : undefined}
                  onSuccess={() => {
                    setShowPandaDocForm(false);
                    void refetch();
                  }}
                  onCancel={() => setShowPandaDocForm(false)}
                />
              )}
            </Box>
          </>
        )}

        {/* Comments — ADR-052 S5: the generic polymorphic comments component
            (subject_type=tasks), gaining replies + inline edit. Collapsed by default
            (legacy task-UX); expands to a bounded box where the thread scrolls with the
            composer pinned. No unmountOnExit: RecordComments holds draft/reply/edit state
            locally, so collapsing must not unmount it (would drop an unsent comment). */}
        <Divider />
        <Box>
          <Stack
            direction="row"
            alignItems="center"
            spacing={0.5}
            sx={{ cursor: "pointer", py: 1 }}
            onClick={() => setCommentsExpanded((prev) => !prev)}
          >
            <Typography variant="subtitle2">Comments ({countComments(taskComments)})</Typography>
            {commentsExpanded ? (
              <ExpandLess fontSize="small" color="action" />
            ) : (
              <ExpandMore fontSize="small" color="action" />
            )}
          </Stack>
          <Collapse in={commentsExpanded}>
            <Box sx={{ height: 420 }}>
              <RecordComments subjectType={ResourceName.TASKS} subjectId={String(task.id)} />
            </Box>
          </Collapse>
        </Box>
      </Stack>

      <ConfirmationDialog
        open={deleteConfirmOpen}
        title="Delete Task"
        message={`Are you sure you want to delete "${task.title}"? This action cannot be undone.`}
        confirmLabel={ConfirmLabelType.Delete}
        confirmColor={ColorType.Error}
        onConfirm={handleDelete}
        onCancel={() => setDeleteConfirmOpen(false)}
      />
    </Dialog>
  );
}
